import React, { useContext, useState, useEffect } from 'react';
import { ThemeContext } from '../../hooks/ThemeContext';
import { UserContext } from '../../hooks/UserContext';
import { api } from '../../services/baseUrl';
import { TrendingUp, Package, DollarSign, Clock, ShoppingCart, Users, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const CreatorStats = () => {
  const { theme } = useContext(ThemeContext);
  const { user } = useContext(UserContext);
  const isDark = theme === 'dark';

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/creator/stats');
      setStats(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Erreur stats:', err);
      toast.error('Erreur lors du chargement des statistiques');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#050506]' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-rose-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#050506]' : 'bg-gray-50'} py-8 px-4`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Statistiques de Ventes
          </h1>
          <p className={`mt-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
            Suivez vos performances et vos revenus
          </p>
        </div>

        {/* Alerte Stripe si pas configuré */}
        {stats && !stats.stripe_status.connected && (
          <div className={`mb-6 p-4 rounded-lg border ${
            isDark ? 'bg-orange-500/10 border-orange-500/30 text-orange-300' : 'bg-orange-50 border-orange-200 text-orange-800'
          }`}>
            <div className="flex items-start gap-3">
              <AlertCircle className="flex-shrink-0 mt-1" size={20} />
              <div>
                <p className="font-semibold">Configurez votre compte Stripe</p>
                <p className="text-sm mt-1">Vous devez configurer votre compte Stripe pour recevoir les paiements de vos ventes.</p>
                <button className="mt-3 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition">
                  Configurer maintenant
                </button>
              </div>
            </div>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Gagné */}
          <div className={`p-6 rounded-xl shadow-lg ${
            isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'
          }`}>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600">
                <DollarSign className="text-white" size={24} />
              </div>
              <div>
                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>Total Gagné</p>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {stats?.total_sales?.toFixed(2) || '0.00'}€
                </p>
              </div>
            </div>
          </div>

          {/* Total Commandes */}
          <div className={`p-6 rounded-xl shadow-lg ${
            isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'
          }`}>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
                <Package className="text-white" size={24} />
              </div>
              <div>
                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>Commandes</p>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {stats?.total_orders || 0}
                </p>
              </div>
            </div>
          </div>

          {/* En attente */}
          <div className={`p-6 rounded-xl shadow-lg ${
            isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'
          }`}>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-gradient-to-br from-orange-500 to-amber-600">
                <Clock className="text-white" size={24} />
              </div>
              <div>
                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>En attente</p>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {stats?.pending_amount?.toFixed(2) || '0.00'}€
                </p>
              </div>
            </div>
          </div>

          {/* Panier Moyen */}
          <div className={`p-6 rounded-xl shadow-lg ${
            isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'
          }`}>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600">
                <ShoppingCart className="text-white" size={24} />
              </div>
              <div>
                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>Panier Moyen</p>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {stats?.total_orders > 0 
                    ? (stats.total_sales / stats.total_orders).toFixed(2) 
                    : '0.00'}€
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Produits */}
          <div className={`p-6 rounded-xl shadow-lg ${
            isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'
          }`}>
            <h3 className={`text-xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Top 5 Produits
            </h3>
            <div className="space-y-4">
              {stats?.top_products?.length > 0 ? (
                stats.top_products.map((item, index) => (
                  <div
                    key={item.product_id}
                    className={`flex items-center justify-between p-4 rounded-lg ${
                      isDark ? 'bg-white/5' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        index === 0 ? 'bg-yellow-500 text-white' :
                        index === 1 ? 'bg-gray-400 text-white' :
                        index === 2 ? 'bg-orange-600 text-white' :
                        isDark ? 'bg-white/10 text-white/60' : 'bg-gray-200 text-gray-600'
                      }`}>
                        {index + 1}
                      </div>
                      <span className={isDark ? 'text-white' : 'text-gray-900'}>
                        {item.product?.name || 'Produit sans nom'}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {item.revenue?.toFixed(2)}€
                      </p>
                      <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                        {item.sales} {item.sales > 1 ? 'ventes' : 'vente'}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className={`text-center py-8 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                  Aucune vente pour le moment
                </p>
              )}
            </div>
          </div>

          {/* Dernières Transactions */}
          <div className={`p-6 rounded-xl shadow-lg ${
            isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'
          }`}>
            <h3 className={`text-xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Transactions Récentes
            </h3>
            <div className="space-y-3">
              {stats?.recent_transactions?.length > 0 ? (
                stats.recent_transactions.slice(0, 5).map((tx) => (
                  <div
                    key={tx.id_transaction}
                    className={`p-3 rounded-lg border ${
                      isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {tx.product?.name}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                          {tx.buyer?.name || 'Acheteur inconnu'}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        tx.status === 'completed' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-orange-500/20 text-orange-400'
                      }`}>
                        {tx.status === 'completed' ? 'Payé' : 'En attente'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                        {new Date(tx.created_at).toLocaleDateString('fr-FR')}
                      </p>
                      <p className={`font-bold text-green-500`}>
                        +{tx.creator_amount?.toFixed(2)}€
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className={`text-center py-8 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                  Aucune transaction
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Info Stripe */}
        {stats?.stripe_status?.connected && (
          <div className={`mt-6 p-6 rounded-xl shadow-lg ${
            isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'
          }`}>
            <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Compte Stripe
            </h3>
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className={isDark ? 'text-white' : 'text-gray-900'}>
                Compte configuré et actif
              </span>
              {stats.stripe_status.verified_at && (
                <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                  (Vérifié le {new Date(stats.stripe_status.verified_at).toLocaleDateString('fr-FR')})
                </span>
              )}
            </div>
            <p className={`mt-4 text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
              Vos paiements sont transférés automatiquement sur votre compte bancaire sous 2-7 jours.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatorStats;
